import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  HeartIcon,
  UserGroupIcon,
  ClockIcon,
  ShieldCheckIcon,
  StarIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";

// --- Swiper Imports ---
// 1. Install Swiper: npm install swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation, EffectFade } from "swiper/modules";

// 2. Import Swiper styles
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";
import "swiper/css/navigation";

// --- Image Imports ---
const doctorImage =
  "https://images.unsplash.com/photo-1622253692010-333f2da6031d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1964&q=80";

// --- Data for Components ---

// Data for the Hero Swiper (Images Only)
const heroSlides = [
  {
    id: 1,
    image: "/1.jpg", // Replace with your local image path or URL
  },
  {
    id: 2,
    image: "/2.jpg", // Replace with your local image path or URL
  },
  {
    id: 3,
    image: "/3.jpg", // Replace with your local image path or URL
  },
  {
    id: 4,
    image: "/4.jpg", // Replace with your local image path or URL
  },
  {
    id: 5,
    image: "/5.jpg", // Replace with your local image path or URL
  },
  {
    id: 6,
    image: "/6.jpg", // Replace with your local image path or URL
  },
];

const services = [
  {
    id: 1,
    icon: <HeartIcon className="w-12 h-12 text-blue-600" />,
    title: "General Dentistry",
    description:
      "Comprehensive care for your entire family, from cleanings to fillings and preventive services.",
  },
  {
    id: 2,
    icon: <UserGroupIcon className="w-12 h-12 text-blue-600" />,
    title: "Cosmetic Dentistry",
    description:
      "Transform your smile with our cosmetic treatments including veneers, whitening, and bonding.",
  },
  {
    id: 3,
    icon: <ClockIcon className="w-12 h-12 text-blue-600" />,
    title: "Orthodontics",
    description:
      "Straighten your teeth with our modern orthodontic solutions including clear aligners and braces.",
  },
  {
    id: 4,
    icon: <ShieldCheckIcon className="w-12 h-12 text-blue-600" />,
    title: "Dental Implants",
    description:
      "Replace missing teeth with the most natural-looking and functioning dental implants.",
  },
];

const testimonials = [
  {
    id: 1,
    name: "Sarah Johnson",
    role: "Patient",
    avatar: "https://randomuser.me/api/portraits/women/12.jpg",
    content:
      "I've been going to Anant Dental for years and have always received exceptional care. The staff is friendly and the doctors are thorough and gentle.",
    rating: 5,
  },
  {
    id: 2,
    name: "Michael Brown",
    role: "Patient",
    avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    content:
      "After years of anxiety about dental visits, I finally found a place where I feel comfortable. The team at Anant Dental truly cares about patient comfort.",
    rating: 5,
  },
  {
    id: 3,
    name: "Jessica Lee",
    role: "Patient",
    avatar: "https://randomuser.me/api/portraits/women/24.jpg",
    content:
      "My entire family receives care at Anant Dental, and we couldn't be happier. From pediatric to adult dentistry, they do it all and do it well!",
    rating: 5,
  },
];

// Component-specific styles for Swiper
const SwiperStyles = () => (
  <style>{`
    .hero-swiper .swiper-button-next,
    .hero-swiper .swiper-button-prev {
      color: #ffffff;
      background-color: rgba(0, 0, 0, 0.3);
      border-radius: 50%;
      width: 44px;
      height: 44px;
      transition: background-color 0.3s ease;
    }
    .hero-swiper .swiper-button-next:hover,
    .hero-swiper .swiper-button-prev:hover {
      background-color: rgba(0, 0, 0, 0.5);
    }
    .hero-swiper .swiper-button-next::after,
    .hero-swiper .swiper-button-prev::after {
      font-size: 18px;
      font-weight: bold;
    }
    .hero-swiper .swiper-pagination-bullet {
      background: rgba(255, 255, 255, 0.7);
      width: 10px;
      height: 10px;
      opacity: 1;
    }
    .hero-swiper .swiper-pagination-bullet-active {
      background: #ffffff;
      width: 12px;
      height: 12px;
    }
  `}</style>
);

const Home: React.FC = () => {
  const [servicesRef, servicesInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [aboutRef, aboutInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [testimonialsRef, testimonialsInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [ctaRef, ctaInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <div className="overflow-hidden">
      <SwiperStyles />
      {/* Hero Section: Full-screen image carousel only */}
      <section className="relative min-h-screen hero-swiper">
        <Swiper
          modules={[Autoplay, Pagination, Navigation, EffectFade]}
          loop={true}
          effect={"fade"}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          pagination={{
            clickable: true,
          }}
          navigation={true}
          className="w-full h-full absolute inset-0"
        >
          {heroSlides.map((slide) => (
            <SwiperSlide key={slide.id}>
              <div className="w-full h-[100vh] bg-cover bg-center">
                <img src={slide.image}></img>
                <div className="absolute inset-0 bg-black/40" />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.span
              initial={{ opacity: 0 }}
              animate={servicesInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5 }}
              className="text-blue-600 font-semibold tracking-wider uppercase"
            >
              Our Services
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={servicesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-3xl md:text-4xl font-bold mt-2 mb-4"
            >
              Comprehensive Dental Care
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={servicesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-gray-600"
            >
              We offer a wide range of dental services to meet all your oral
              health needs
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="mb-6">{service.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section ref={aboutRef} className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={aboutInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <img
                src={"9.jpg"}
                alt="Dental professional"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={aboutInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <span className="text-blue-600 font-semibold tracking-wider uppercase">
                About Us
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
                Your Trusted Dental Care Partner
              </h2>
              <p className="text-gray-600 mb-8">
                At Anant Dental, we combine cutting-edge technology with
                compassionate care to provide you with the best dental
                experience. Our team of experienced professionals is dedicated
                to ensuring your comfort and satisfaction.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <UserGroupIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Expert Team</h4>
                    <p className="text-gray-600">
                      Highly qualified professionals
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <ShieldCheckIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Quality Care</h4>
                    <p className="text-gray-600">Premium dental services</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section ref={testimonialsRef} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.span
              initial={{ opacity: 0 }}
              animate={testimonialsInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5 }}
              className="text-blue-600 font-semibold tracking-wider uppercase"
            >
              Testimonials
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-3xl md:text-4xl font-bold mt-2 mb-4"
            >
              What Our Patients Say
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg"
              >
                <div className="flex items-center mb-6">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full mr-4"
                  />
                  <div>
                    <h4 className="font-semibold">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.role}</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon
                      key={i}
                      className="w-5 h-5 text-yellow-400 fill-current"
                    />
                  ))}
                </div>
                <p className="text-gray-600">{testimonial.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={ctaInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
              className="text-3xl md:text-4xl font-bold mb-6"
            >
              Ready to Transform Your Smile?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={ctaInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-xl mb-8 text-blue-100"
            >
              Book your appointment today and take the first step towards a
              healthier, more beautiful smile.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={ctaInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/appointment"
                className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-full hover:bg-blue-50 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Your Appointment
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
